import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Region } from '../../services/dtos/region';
import { Injectable } from '@angular/core';
import { RegionsService } from 'src/app/services/regions.service';
import { RegionBasicInfo } from 'src/app/services/dtos/regionBasicInfo';
import {
  append,
  iif,
  insertItem,
  patch,
  removeItem,
  updateItem,
} from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AddRegion,
  AddRegionEsb as AddRegionEsb,
  ClearCurrentRegionId,
  DeleteRegion,
  DeleteRegionEsb as DeleteRegionEsb,
  FetchAllRegionsBasicInfo,
  FetchRegion,
  SetCurrentRegionId,
  UpdateRegion,
  UpdateRegionEsb as UpdateRegionEsb,
} from './regions.actions';

export class RegionsStateModel {
  public regions!: Region[];
  public currentUserRegions!: Region[];
  public selectedRegionId!: string;
  public allRegions!: Region[];
}

const defaults = {
  regions: [],
  currentUserRegions: [],
  selectedRegionId: '',
  allRegions: [],
};

@State<RegionsStateModel>({
  name: 'regionsState',
  defaults,
})
@Injectable()
export class RegionsState {
  public constructor(private regionsService: RegionsService) {}

  @Selector()
  public static getRegionsBasicInfo(
    state: RegionsStateModel
  ): RegionBasicInfo[] {
    return state.regions
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name)) as RegionBasicInfo[];
  }

  @Selector()
  public static getSelectedRegionId(
    state: RegionsStateModel
  ): string | undefined {
    return state.selectedRegionId;
  }

  @Selector()
  public static getSelectedRegion(
    state: RegionsStateModel
  ): Region | undefined {
    return state.regions.find((c) => c.id === state.selectedRegionId);
  }

  @Action(FetchAllRegionsBasicInfo)
  public fetchAll({
    getState,
    setState,
  }: StateContext<RegionsStateModel>): Observable<Region[]> {
    return this.regionsService.getAllBasicInfo().pipe(
      tap((freshRegions: Region[]) => {
        const state = getState();
        // add or update
        freshRegions.forEach((region) => {
          setState(
            patch<RegionsStateModel>({
              regions: iif<Region[]>(
                (regions) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  regions!.some((x) => x.id === region.id),
                updateItem<Region>(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (x) => x!.id === region.id,
                  region
                ),
                insertItem<Region>(region)
              ),
            })
          );
        });
        // remove
        const regionToRemove = state.regions.filter((c) =>
          freshRegions.every((fc) => fc.id !== c.id)
        );
        regionToRemove.forEach((region) => {
          setState(
            patch<RegionsStateModel>({
              regions: removeItem<Region>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === region.id
              ),
            })
          );
        });
      })
    );
  }

  @Action(AddRegion)
  public add(
    { setState }: StateContext<RegionsStateModel>,
    payload: AddRegion
  ): Observable<string> {
    return this.regionsService.addRegion(payload.code, payload.name).pipe(
      tap((regionId) => {
        const newRegion = {
          ...payload,
          id: regionId,
          laboratories: 0,
          esbs: [],
        } as Region;
        setState(
          patch({
            regions: append<Region>([newRegion]),
            selectedRegionId: regionId,
          })
        );
      })
    );
  }

  @Action(UpdateRegion)
  public update(
    { setState, getState }: StateContext<RegionsStateModel>,
    payload: UpdateRegion
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    return this.regionsService
      .updateRegion(payload.id, payload.code, payload.name)
      .pipe(
        tap(() => {
          const state = getState();
          const region = state.regions.find((x) => x.id === payload.id);
          const updated = {
            ...region,
            code: payload.code,
            name: payload.name,
          } as Region;
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
              regions: updateItem<Region>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === payload.id,
                updated
              ),
            })
          );
        })
      );
  }

  @Action(FetchRegion)
  public fetchSingle(
    { setState }: StateContext<RegionsStateModel>,
    { id }: FetchRegion
  ): Observable<Region> {
    return this.regionsService.getRegion(id).pipe(
      tap((region: Region) => {
        setState(
          patch<RegionsStateModel>({
            regions: iif<Region[]>(
              (regions) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                regions!.some((x) => x.id === region.id),
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              updateItem<Region>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === region.id,
                region
              ),
              insertItem<Region>(region)
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentRegionId)
  public clearCurrentRegionId({
    setState,
  }: StateContext<RegionsStateModel>): void {
    setState(
      patch({
        selectedRegionId: '',
      })
    );
  }

  @Action(DeleteRegion)
  public delete(
    { setState }: StateContext<RegionsStateModel>,
    { id }: DeleteRegion
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.regionsService.deleteRegion(id).pipe(
      tap(() => {
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            regions: removeItem<Region>((x) => x!.id === id),
          })
        );
      })
    );
  }

  @Action(SetCurrentRegionId)
  public setCurrentRegionId(
    { setState }: StateContext<RegionsStateModel>,
    { id }: SetCurrentRegionId
  ): void {
    setState(
      patch({
        selectedRegionId: id,
      })
    );
  }

  @Action(DeleteRegionEsb)
  public deleteRegionConnection(
    { dispatch }: StateContext<RegionsStateModel>,
    { regionId, esbId: esbId }: DeleteRegionEsb
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.regionsService
      .deleteRegionEsb(regionId, esbId)
      .pipe(tap(() => dispatch(new FetchRegion(regionId))));
  }

  @Action(AddRegionEsb)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addRegionEsb(
    { dispatch }: StateContext<RegionsStateModel>,
    { payload }: AddRegionEsb
  ): Observable<string> {
    return this.regionsService
      .addRegionEsb(payload)
      .pipe(tap(() => dispatch(new FetchRegion(payload.locationId))));
  }

  @Action(UpdateRegionEsb)
  public updateRegionEsb(
    { dispatch }: StateContext<RegionsStateModel>,
    { payload }: UpdateRegionEsb
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.regionsService
      .updateRegionEsb(payload)
      .pipe(tap(() => dispatch(new FetchRegion(payload.locationId))));
  }
}
